import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { EditorWithCounter } from '@/components/form-box-editor';
import { FormInputSelect } from '@/components/form-input-select/FormInputSelect';
import { FormInput } from '@/components/form-input/FormInput';
import { FormSelect, TagRenderComponent } from '@/components/form-select/FormSelect';
import FormEditor from '@components/form-box-editor/FormEditor';

import { API } from '@/utils/constants/Apis';
import { NOTE_MAX_LENGTH } from '@/utils/constants/AppConstants';
import { getSupportTypeMemoList } from '@/utils/services/ProjectApiService';

import { axiosInstance } from '@/config/http';

import { IOptions, ISupportType } from '../model';

const HEADER_HEIGHT = 52 + 12;
const OUTSIDE_PADDING = 20 * 2;
const BODY_PADDING = 24 * 2;
const ANOTHER_SECTION_HEIGHT = 32 + 12 + 32 + 12 + 32 + 12 + 70 + 12 + 70 + 12;
const NOTE_LABEL_HEIGHT = 30;
const OUTSIDE_NOTE_HEIGHT = HEADER_HEIGHT + OUTSIDE_PADDING + BODY_PADDING + ANOTHER_SECTION_HEIGHT + NOTE_LABEL_HEIGHT + 8;

interface IPropsMemo {
  categoryOptions: IOptions[];
  countryId?: string;
  changePicReasonInfo?: string;
  onCategoryAdded?: (newCategory: IOptions) => void;
  selectedCategoryLabelMap?: Map<string, string>;
}

export const ProjectMemoForm = ({
  categoryOptions,
  countryId,
  changePicReasonInfo,
  onCategoryAdded,
  selectedCategoryLabelMap = new Map()
}: IPropsMemo) => {
  const methods = useFormContext();
  const { t } = useTranslation();
  const [supportTypeOptions, setSupportTypeOptions] = useState<IOptions[]>([]);
  const { id } = useParams();

  useEffect(() => {
    getSupportTypeData();
  }, []);

  const eMoNumber = methods?.getValues('emoNumber');
  const folderValue = methods?.getValues('folder');
  const location = methods?.getValues('location');
  const memoValue = methods?.watch('memo');
  const changePicReason = methods.watch('changePicReason');

  useEffect(() => {
    if (!changePicReason && changePicReasonInfo) {
      methods.setValue('changePicReason', changePicReasonInfo);
    }
  }, [changePicReason, changePicReasonInfo, methods]);

  const getNextCategoryOrder = async () => {
    if (!countryId || !id) {
      return 1;
    }

    try {
      const response = await axiosInstance.get(API.GET_CATEGORY(countryId, 'PROJECT', id));
      const categories = Array.isArray(response?.data) ? response.data : response?.data?.data ?? [];
      const maxOrder = categories.reduce((max: number, item: any) => {
        const order = Number(item?.displayOrder ?? item?.order ?? 0);
        return Number.isFinite(order) ? Math.max(max, order) : max;
      }, 0);

      return maxOrder > 0 ? maxOrder + 1 : categories.length + 1;
    } catch (error) {
      return 1;
    }
  };

  const getSupportTypeData = async () => {
    try {
      const res = await getSupportTypeMemoList();
      if (res?.data) {
        const dataTemp = res.data.map((item: ISupportType) => {
          return {
            label: item.name,
            value: item.id,
            id: item.id
          };
        });
        setSupportTypeOptions(dataTemp);
      }
    } catch (error) {}
  };

  const handleVisaCategoryChange = (selectedValues: string[]) => {
    const dedupedValues = Array.from(
      selectedValues
        .reduce((map, selectedValue) => {
          const [id] = selectedValue.split('::');
          if (id) {
            map.set(String(id), selectedValue);
          }
          return map;
        }, new Map<string, string>())
        .values()
    );

    methods.setValue('visaCategories', dedupedValues as any);
  };

  const visaCategoryTagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const selectedValue = String(value ?? '');
    const [selectedId, currentName] = selectedValue.split('::');
    const oldLabel = selectedCategoryLabelMap.get(String(selectedId));
    const displayLabel = oldLabel && oldLabel === currentName ? oldLabel : label;

    return (
      <TagRenderComponent
        label={displayLabel}
        closable={closable}
        showCloseItem={false}
        tagShowTooltip={false}
        rest={{}}
        tagMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        tagClosing={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClose?.();
        }}
        props={props}
      />
    );
  };

  return (
    <div className='grow flex flex-col gap-[12px]'>
      <div className='flex flex-row gap-[18px]'>
        <div className='basis-1/2'>
          <FormInput
            defaultValue={location}
            name='location'
            labelTx={t('project_memo:location')}
            placeholderTx={t('placeholder:text_box', { field: t('project_memo:location') })}
            className='!h-[40px]'
          />
        </div>
        <div className='basis-1/2'>
          <FormSelect
            name='visaCategories'
            mode='multiple'
            label={t('project_memo:visa_category')}
            placeholder={t('placeholder:select', { field: t('project_memo:visa_category') })}
            options={categoryOptions}
            allowAddOnSearch
            handleChange={(value) => handleVisaCategoryChange(Array.isArray(value) ? value : value ? [value] : [])}
            tagRender={visaCategoryTagRender}
            addRequest={{
              mappingParams: async (inputValue: string) => {
                const nextOrder = await getNextCategoryOrder();

                return {
                  name: inputValue,
                  countryId,
                  referenceOriginId: id || null,
                  referenceOriginType: 'PROJECT',
                  order: nextOrder,
                  displayOrder: nextOrder
                };
              },
              method: 'POST',
              apiEndPoint: API.ADD_CATEGORY(),
              defaultParams: {}
            }}
            onAddNew={(item) => onCategoryAdded?.(item as IOptions)}
            className='!min-h-[36px] w-full [&:not(.non-border)]:!border-none'
          />
        </div>
      </div>
      <div className='flex flex-row gap-[18px]'>
        <div className='basis-1/2'>
          <FormInput
            defaultValue={eMoNumber}
            name='emoNumber'
            labelTx={t('project_memo:e_mo_number')}
            placeholderTx={t('placeholder:text_box', { field: t('project_memo:e_mo_number') })}
          />
        </div>
        <div className='basis-1/2'>
          <FormInputSelect
            name='supportTypeId'
            label={t('project_memo:support_type')}
            placeholder={t('placeholder:select', { field: t('project_memo:support_type') })}
            options={supportTypeOptions}
            overflowTooltip='unset'
          />
        </div>
      </div>
      <div>
        <p className='subtitle-14 mb-2'>{t('project_memo:folder')}</p>
        <FormEditor
          name='folder'
          defaultValue={folderValue}
          toolbar={null}
          editorWrapperProps={{ className: '!rounded-[10px]' }}
          placeholder={t('placeholder:text_box', { field: t('project_memo:folder') })}
        />
      </div>
      <div className='grow'>
        <EditorWithCounter
          label={t('project_memo:memo')}
          name='memo'
          limit={NOTE_MAX_LENGTH}
          defaultValue={memoValue}
          className='h-full'
          placeholder={t('placeholder:text_box', { field: t('project_memo:memo') })}
          editorWrapperProps={{ style: { height: `calc(100vh - ${OUTSIDE_NOTE_HEIGHT}px)`, minHeight: 280 } }}
        />
      </div>
      <div className='grow'>
        <EditorWithCounter
          label={t('project_memo:message_memo')}
          name='changePicReason'
          limit={NOTE_MAX_LENGTH}
          defaultValue={changePicReasonInfo}
          className='h-full'
          placeholder={t('placeholder:text_box', { field: t('project_memo:message_memo') })}
          editorWrapperProps={{ style: { height: `calc(100vh - ${OUTSIDE_NOTE_HEIGHT}px)`, minHeight: 280 } }}
        />
      </div>
    </div>
  );
};
